import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as action from "../Store/_redux/LeaveStore/leaveAction";
import * as authAction from "../Store/_redux/AuthStore/authActions";
import { LeaveStateEnum, leaveStateKey } from "../modal/leave-state";
import { LeaveTypeEnum, leaveTypeLabel } from "../modal/leave-type";
import ProfileCard from "../components/ProfileCard";
import { useTranslation } from "react-i18next";

const stateBadgeColor = {
  [LeaveStateEnum.APPROVED]: "bg-green-500",
  [LeaveStateEnum.REJECTED]: "bg-red-500",
  [LeaveStateEnum.PENDING]: "bg-yellow-400",
};

function Dashboard() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth.loginUser);
  const formLeave = useSelector((state) => state.leave.leaves) || [];
  const usersFromStore = useSelector((state) => state.auth.users) || [];
  const formLeaveTransaction = useSelector((state) => state.leave.transactions) || [];
  const [systemDeleting, setSystemDeleting] = useState(false);

  useEffect(() => {
    if(!auth) return;
    dispatch(action.getLeave(auth));
    dispatch(action.getLeaveTransactions(auth.tc));
    if (auth?.userType === "admin") {
      dispatch(authAction.getUsers());
    }
  }, [auth]);

  // İstatistik kartları için sayılar
  const total = formLeave.length;
  const approved = formLeave.filter((l) => l?.state === LeaveStateEnum.APPROVED).length;
  const pending = formLeave.filter((l) => l?.state === LeaveStateEnum.PENDING).length;
  const rejected = formLeave.filter((l) => l?.state === LeaveStateEnum.REJECTED).length;
  const handleSystemDelete = async () => {
    const systemCount = formLeaveTransaction.filter((l) => l?.actionType === "SYSTEM").length;
    if (systemCount === 0) {
      toast.info(t("dashboard.systemDeleteNone"));
      return;
    }
    if (!window.confirm(t("dashboard.systemDeleteConfirm", { count: systemCount }))) return;

    setSystemDeleting(true);
    const result = await dispatch(action.deleteSystemLeaveTransactions());
    setSystemDeleting(false);

    if (!result) {
      toast.error(t("dashboard.systemDeleteError"));
      return;
    }
    dispatch(action.getLeaveTransactions(auth.tc));
    dispatch(authAction.getUsers());
    toast.success(t("dashboard.systemDeleteSuccess"));
  };

  const statCards = [
    { label: t("dashboard.statTotal"), value: total, color: "bg-blue-500" },
    { label: t("dashboard.statPending"), value: pending, color: "bg-yellow-600" },
    { label: t("dashboard.statApproved"), value: approved, color: "bg-green-500" },
    { label: t("dashboard.statRejected"), value: rejected, color: "bg-red-500" },
  ];

  // Her kullanıcı için izin istatistiklerini hesapla
  const userStats = usersFromStore.map((user) => {
    const userLeaves = formLeave.filter((l) => l?.owner?.tc === user.tc);

    // Diğer izin türleri (sadece onaylanmış)
    const sick = userLeaves
      .filter((l) => l?.leavesType === LeaveTypeEnum.SICK && l?.state === LeaveStateEnum.APPROVED)
      .reduce((sum, l) => sum + (l.totalDate || 0), 0);
    const unpaidCount = userLeaves
      .filter((l) => l?.leavesType === LeaveTypeEnum.UNPAID && l?.state === LeaveStateEnum.APPROVED)
      .reduce((sum, l) => sum + (l.totalDate || 0), 0);
    const maternity = userLeaves
      .filter((l) => l?.leavesType === LeaveTypeEnum.MATERNITY && l?.state === LeaveStateEnum.APPROVED)
      .reduce((sum, l) => sum + (l.totalDate || 0), 0);

    return {
      ...user,
      totalAnnual: (user.remainingLeaveDays || 0) + (user.usedLeaveDays || 0),
      remainingAnnual: user.remainingLeaveDays || 0,
      usedAnnual: user.usedLeaveDays || 0,
      sick,
      unpaidCount,
      maternity,
    };
  });

  // Son 5 izin isteği
  const recent = [...formLeave].slice(0, 5);

  const stateLabel = (state) => {
    const key = leaveStateKey(state);
    return <span className={`${stateBadgeColor[key]} text-white text-xs px-2 py-1 rounded`}>{t(`leaveState.${key}`)}</span>;
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-semibold mb-6">{t("dashboard.title")}</h1>
      {auth?.userType === "admin" && (
        <button
          className="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded disabled:bg-blue-300"
          onClick={handleSystemDelete}
          disabled={systemDeleting}
        >
          {systemDeleting ? t("dashboard.systemDeleting") : t("dashboard.systemDelete")}
        </button>
      )}

      {/* Profil Kartı ve İstatistik Kartları */}
      <div className="flex gap-6 mb-8 items-start">
        <div className="flex-1">
          <ProfileCard />
        </div>
        <div className="grid grid-cols-2 gap-3 self-center flex-shrink-0">
          {statCards.map((card, i) => (
            <div
              key={i}
              className={`${card.color} text-white rounded-lg p-4 flex flex-col justify-between w-32 h-32`}
            >
              <p className="text-base font-medium leading-tight">{card.label}</p>
              <p className="text-4xl font-bold">{card.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Admin için Çalışan İzin Durumları Tablosu */}
      {auth?.userType === "admin" && (
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">{t("dashboard.empStats")}</h2>
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left">{t("dashboard.fullName")}</th>
                  <th className="px-4 py-3 text-left">{t("dashboard.tckn")}</th>
                  <th className="px-4 py-3 text-left">{t("dashboard.totalAnnualLimit")}</th>
                  <th className="px-4 py-3 text-left">{t("dashboard.remainingAnnual")}</th>
                  <th className="px-4 py-3 text-left">{t("dashboard.usedAnnual")}</th>
                  <th className="px-4 py-3 text-left">{t("dashboard.usedSick")}</th>
                  <th className="px-4 py-3 text-left">{t("dashboard.usedUnpaid")}</th>
                  <th className="px-4 py-3 text-left">{t("dashboard.usedMaternity")}</th>
                </tr>
              </thead>
              <tbody>
                {userStats.map((user) => (
                  <tr key={user._id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">
                      {user.name} {user.surname}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{user.tc}</td>
                    <td className="px-4 py-3">{user.totalAnnual}</td>
                    <td className="px-4 py-3">{user.remainingAnnual}</td>
                    <td className="px-4 py-3">{user.usedAnnual}</td>
                    <td className="px-4 py-3">{user.sick}</td>
                    <td className="px-4 py-3">{user.unpaidCount}</td>
                    <td className="px-4 py-3">{user.maternity}</td>
                  </tr>
                ))}
                {userStats.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-gray-500">
                      {t("dashboard.noUsers")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Son İzin İstekleri */}
      <h2 className="text-2xl font-semibold mb-4">{t("dashboard.recentRequests")}</h2>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left px-4 py-3">{t("dashboard.fullName")}</th>
              <th className="text-left px-4 py-3">{t("dashboard.tckn")}</th>
              <th className="text-left px-4 py-3">{t("dashboard.status")}</th>
              <th className="text-left px-4 py-3">{t("dashboard.type")}</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((item, i) => (
              <tr
                key={i}
                className="border-t hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/detaylar/${item._id}`)}
              >
                <td className="px-4 py-3 font-medium">
                  {item?.owner?.name} {item?.owner?.surname}
                </td>
                                 <td className="px-4 py-3 text-gray-500">{item?.owner?.tc}</td>
                <td className="px-4 py-3">{stateLabel(item?.state)}</td>
                <td className="px-4 py-3">{leaveTypeLabel(t, item?.leavesType, "-")}</td>
              </tr>
            ))}
            {recent.length === 0 && (
              <tr>
                <td colSpan="4" className="text-center py-4 text-gray-500">
                  {t("dashboard.noRequests")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Dashboard;